<script setup lang="ts">
import {
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonTitle,
    IonContent,
    IonNote,
    IonList,
    IonItem,
    IonLabel,
    IonCheckbox,
    IonRippleEffect,
    IonSpinner,
    IonIcon,
    modalController,
    alertController,
} from "@ionic/vue";
import { ref, computed, PropType } from "vue";
import { useCostumersStore } from "@/stores/costumers";
import { useRoute } from "vue-router";
import { IZone } from "@/types/zone";
import { ICostumer } from "@/types/costumer";
import { reload, toggle } from "ionicons/icons";

const props = defineProps({
    zone: {
        type: Object as PropType<IZone>,
        required: true,
    },
});
const selectedCostumers = ref<ICostumer[]>([]);
const costumerStore = useCostumersStore();
const customers = computed(() =>
    costumerStore.costumers
        .map((c) => ({
            ...c,
            zone_name: c.zone
                ? costumerStore.getZoneById(c.zone!)?.name || "Nessuna area assegnata"
                : "Nessuna area assegnata",
        }))
        .sort((a, b) => {
            if (a.zone_name === "Nessuna area assegnata" && b.zone_name !== "Nessuna area assegnata") {
                return -1;
            }
            if (a.zone_name !== "Nessuna area assegnata" && b.zone_name === "Nessuna area assegnata") {
                return 1;
            }
            return a.zone_name.localeCompare(b.zone_name);
        }),
);

const toggleCostumer = (costumer: ICostumer) => {
    const index = selectedCostumers.value.findIndex((c) => c.id === costumer.id);
    if (index !== -1) {
        selectedCostumers.value.splice(index, 1);
    } else {
        selectedCostumers.value.push(costumer);
    }
};
const overrideAlert = async () => {
    const alert = await alertController.create({
        header: "Assegna clienti all'area",
        message: `Alcuni dei clienti selezionati sono già assegnati ad un altra area. Sovrascrivere?`,
        buttons: [
            {
                text: "Annulla",
                role: "cancel",
                cssClass: "archive-text-medium",
            },
            {
                text: "Sovrascrivi",
                cssClass: "archive-text-danger",
                handler: async () => {
                    await costumerStore.assignCostumersToZone(
                        selectedCostumers.value.map((c) => c.id),
                        props.zone.id,
                    );
                    return modalController.dismiss(null, "confirm");
                },
            },
        ],
    });
    await alert.present();
};
const cancel = () => {
    return modalController.dismiss(null, "cancel");
};
const submit = async () => {
    // If no costumers selected, just cancel
    if (selectedCostumers.value.length === 0) {
        return modalController.dismiss(null, "cancel");
    }
    // Check if any selected costumer is already assigned to a zone
    if (selectedCostumers.value.some((c) => c.zone != null)) {
        await overrideAlert();
    } else {
        // Send a post request to assign selected costumers to the zone
        await costumerStore.assignCostumersToZone(
            selectedCostumers.value.map((c) => c.id),
            props.zone.id,
        );
        // reload zone store data
        costumerStore.loadZones();
        return modalController.dismiss(null, "confirm");
    }
};
</script>
<template>
    <ion-header>
        <ion-toolbar>
            <ion-buttons slot="start">
                <ion-button color="medium" @click="cancel">Annulla</ion-button>
            </ion-buttons>
            <ion-title>Assegna clienti all'area</ion-title>
            <ion-buttons slot="end">
                <ion-button @click="submit" :strong="true">Conferma</ion-button>
            </ion-buttons>
        </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding" style="--background: var(--ion-item-background)">
        <ion-list>
            <ion-item
                v-for="customer in customers"
                :key="customer.id"
                @click="toggleCostumer(customer)"
                :detail="false"
                :class="{ 'ion-activated': selectedCostumers.includes(customer) }">
                <ion-checkbox
                    :checked="selectedCostumers.map((c) => c.id).includes(customer.id)"
                    style="pointer-events: none"
                    :aria-label="`select costumer-${customer.id}`"
                    slot="start"></ion-checkbox>
                <ion-label>
                    <ion-label>{{ customer.business_name }}</ion-label>
                    <ion-note
                        :style="{
                            color:
                                customer.zone_name == 'Nessuna area assegnata'
                                    ? 'var(--ion-color-primary)'
                                    : 'var(--ion-color-step-600)',
                        }">
                        {{ customer.zone_name }}
                    </ion-note>
                </ion-label>
            </ion-item>
        </ion-list>
    </ion-content>
</template>
